/**
 * LLM Scans List Page
 *
 * Displays all user scans in a filterable, sortable table with pagination.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Filter, Plus, AlertCircle } from "lucide-react";
import { useLLMScans } from "@/hooks/useLLMScans";
import {
  formatTimestamp,
  formatDuration,
  getStatusColor,
  formatStatus,
} from "@/utils/llmFormatters";
import type { LLMScanStatus } from "@/types/llm";

export function LLMScansListPage() {
  useApi();

  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<LLMScanStatus | undefined>();
  const [providerFilter, setProviderFilter] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState("");

  const { data, isLoading, error } = useLLMScans({
    page,
    items_per_page: 20,
    status: statusFilter,
    provider_type: providerFilter,
  });

  const handleScanClick = (scanUuid: string) => {
    navigate(`/llm-scans/${scanUuid}/results`);
  };

  const handleStatusFilter = (status: LLMScanStatus | "all") => {
    setStatusFilter(status === "all" ? undefined : status);
    setPage(1); // Reset to first page when filter changes
  };

  const handleProviderFilter = (provider: string | "all") => {
    setProviderFilter(provider === "all" ? undefined : provider);
    setPage(1);
  };

  // Use API-paginated data directly (search filtering should be done server-side)
  // For now, we'll do client-side search filtering but keep pagination working
  const displayedScans =
    data?.data.filter((scan) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          scan.uuid.toLowerCase().includes(query) ||
          scan.provider_type?.toLowerCase().includes(query)
        );
      }
      return true;
    }) || [];

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <div>
                <div className="font-medium">Error loading scans</div>
                <div className="text-sm text-muted-foreground">
                  {error.message || "Unknown error"}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold">AI Compliance Scans</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            View and manage your scan results
          </p>
        </div>
        <Button onClick={() => navigate("/compliance")} className="text-xs sm:text-sm whitespace-nowrap">
          <Plus className="h-4 w-4 mr-2" />
          New Scan
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Search by UUID or provider type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Status</DropdownMenuLabel>
                <DropdownMenuCheckboxItem
                  checked={!statusFilter}
                  onCheckedChange={() => handleStatusFilter("all")}
                >
                  All
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={statusFilter === "pending"}
                  onCheckedChange={() => handleStatusFilter("pending")}
                >
                  Pending
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={statusFilter === "running"}
                  onCheckedChange={() => handleStatusFilter("running")}
                >
                  Running
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={statusFilter === "completed"}
                  onCheckedChange={() => handleStatusFilter("completed")}
                >
                  Completed
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={statusFilter === "failed"}
                  onCheckedChange={() => handleStatusFilter("failed")}
                >
                  Failed
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={statusFilter === "cancelled"}
                  onCheckedChange={() => handleStatusFilter("cancelled")}
                >
                  Cancelled
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Provider Type</DropdownMenuLabel>
                <DropdownMenuCheckboxItem
                  checked={!providerFilter}
                  onCheckedChange={() => handleProviderFilter("all")}
                >
                  All
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={providerFilter === "openai"}
                  onCheckedChange={() => handleProviderFilter("openai")}
                >
                  OpenAI
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={providerFilter === "bedrock"}
                  onCheckedChange={() => handleProviderFilter("bedrock")}
                >
                  Bedrock
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={providerFilter === "openai_compatible"}
                  onCheckedChange={() => handleProviderFilter("openai_compatible")}
                >
                  OpenAI Compatible
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Scans Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Scans {data && `(${data.total_count} total)`}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : displayedScans.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <AlertCircle className="h-12 w-12 mb-4 text-muted-foreground opacity-50" />
              <p className="text-sm font-medium mb-1">No scans found</p>
              <p className="text-xs text-muted-foreground">
                {searchQuery || statusFilter || providerFilter
                  ? "Try adjusting your filters or search query"
                  : "Create your first scan to get started"}
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">Date</TableHead>
                      <TableHead className="text-xs sm:text-sm">Provider</TableHead>
                      <TableHead className="text-xs sm:text-sm">Status</TableHead>
                      <TableHead className="text-xs sm:text-sm">Vulnerabilities</TableHead>
                      <TableHead className="text-xs sm:text-sm">Duration</TableHead>
                      <TableHead className="text-xs sm:text-sm">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedScans.map((scan) => (
                      <TableRow
                        key={scan.uuid}
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleScanClick(scan.uuid)}
                      >
                        <TableCell className="font-medium text-xs sm:text-sm">
                          <span className="whitespace-nowrap">
                            {formatTimestamp(scan.created_at)}
                          </span>
                        </TableCell>
                        <TableCell className="capitalize text-xs sm:text-sm">
                          {scan.provider_type}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusColor(scan.status)} text-xs`}>
                            {formatStatus(scan.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          {scan.failed_tests > 0 ? (
                            <span className="text-red-600 font-medium">{scan.failed_tests}</span>
                          ) : (
                            <span className="text-green-600">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm whitespace-nowrap">
                          {scan.duration_seconds !== null
                            ? formatDuration(scan.duration_seconds)
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleScanClick(scan.uuid);
                            }}
                          >
                            <span className="hidden sm:inline">View Results</span>
                            <span className="sm:hidden">View</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {data && data.total_count > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t">
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    Showing {data.data.length} of {data.total_count} scans
                    {data.total_count > 20 &&
                      ` (Page ${page} of ${Math.ceil(data.total_count / 20)})`}
                  </div>
                  {data.total_count > 20 && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 1 || isLoading}
                        onClick={() => {
                          setPage((p) => Math.max(1, p - 1));
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="text-xs sm:text-sm min-w-[80px]"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= Math.ceil(data.total_count / 20) || isLoading}
                        onClick={() => {
                          setPage((p) => p + 1);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="text-xs sm:text-sm min-w-[80px]"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
